import { FC, Dispatch, SetStateAction, useState, useMemo } from 'react'
import { AttorneyData, FormerEmployerData } from '../../../dtos/applicationData'
import { AboutAttorneyFormContainer, AttorneyWorkHistoryWithoutResume } from '../defaultComponents'
import { LeftBar } from '../../basic/LeftBar'
import StandardInput from '../../basic/StandardInput'
import StandardFormatInput from '../../basic/StandardFormatInput'
import StandardSelect from '../../basic/StandardSelect'
import Button from '../../basic/Button'
import StepBullet from '../../basic/StepBullet'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { aboutAttorneyPosition } from '../../../constants/application'
import { object, string, number, bool } from 'yup'
import { validateDateField } from 'src/utils/formDataManipulation'

export type HistorySectionData = AttorneyData['workHistory']

interface AttorneyFormHistorySectionProps {
  historySectionData: HistorySectionData;
  setHistorySectionData: Dispatch<SetStateAction<HistorySectionData>>;
  handlePageResize: () => void;
  formInitialValidation: boolean;
  onSaveDetails: () => Promise<void>;
  isOpen: boolean;
  handleOpen: () => void;
  showErrors?: boolean;
}

const cleanFormerEmployer: FormerEmployerData = {
  firmName: '',
  position: '',
  employmentDateFrom: '',
  employmentDateTo: '',
}

const AttorneyFormHistorySection: FC<AttorneyFormHistorySectionProps> = ({
  historySectionData,
  setHistorySectionData,
  handlePageResize,
  formInitialValidation,
  onSaveDetails,
  isOpen,
  handleOpen,
  showErrors
}) => {
  const [isAccordion, setIsAccordion] = useState<boolean>(formInitialValidation)
  const [disabledSaveDetailsButton, setDisabledSaveDetailsButton] = useState(false)
  
  const handleChange = (name: string, value: any) => {
    setHistorySectionData(prev => ({ ...prev, [name]: value }))
  }

  const formContainerClasses = `form-container ${
    isAccordion ? 
    (isOpen ? 'accordion-open' : 'accordion-closed') :
    ''
  }`

  const handleSaveDetails = async () => {
    setDisabledSaveDetailsButton(true)
    await onSaveDetails()
    setIsAccordion(true)
    handlePageResize()
    setDisabledSaveDetailsButton(false)
  }
  
  const handleToggleAccordion = () => {
    handleOpen()
    handlePageResize()
  }
  const validationSchema = object({
    isResumeAvailable: bool().required('isResumeAvailable'),
    workHistory: object().when('isResumeAvailable', {
      is: true,
      then: sch => sch.shape({
        joinedDate: string().validDate('historySection').required('historySection'),
        hoursPerWeek: number().required('historySection')
      }).required('historySection'),
      otherwise: sch => sch.shape({
        joinedDate: string().validDate('historySection').required('historySection'),
        hoursPerWeek: number().moreThan(0,'historySection').required('historySection'),
      }).required('historySection')
    })
  })

  const isFormValid = useMemo(() => {
    try {
      validationSchema.validateSync({
        workHistory: historySectionData
      })
      return true
    } catch(e) {
      return false
    }
  }, [historySectionData, validationSchema])

  const isInputIncorrect = () => {
    return showErrors && !isFormValid
  }

  return (
    <AboutAttorneyFormContainer>
      <LeftBar />
      <div className={formContainerClasses}>
        <div className='form-title'>
          <StepBullet isComplete>1</StepBullet>
          <h6>WORK HISTORY</h6>
          <button onClick={handleToggleAccordion}>
            {isOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdKeyboardArrowDown />
            )}
          </button>
        </div>
        <div className='form-content'>
          <StandardFormatInput
            label='Date joined present firm'
            maskStyle='date'
            leftIcon='calendar'
            value={historySectionData?.joinedDate ?? ''}
            onValueChange={({ formattedValue }) => handleChange('joinedDate', formattedValue)}
            isIncorrect={isInputIncorrect() && !historySectionData?.joinedDate}
            errorMessage={isInputIncorrect() ? validateDateField(historySectionData?.joinedDate) : ''}
          />
          <StandardInput
            label={<span>Average hours worked per week for this firm (Not Just Billable Hours)</span>}
            type="number"
            value={String(historySectionData?.hoursPerWeek ?? 0)}
            onChange={({ target: { value } }) => handleChange('hoursPerWeek', isNaN(Number(value)) ? undefined : Number(value))}
            isIncorrect={isInputIncorrect() && !historySectionData?.hoursPerWeek}
            errorMessage={isInputIncorrect() && !historySectionData?.hoursPerWeek ? 'This is a required field' : ''}
            min={0}
          />
          <Button 
            onClick={() => handleSaveDetails()}
            disabled={disabledSaveDetailsButton ? true : !isFormValid}
          >
            {disabledSaveDetailsButton ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </AboutAttorneyFormContainer>
  )
}

export default AttorneyFormHistorySection